/**
 * Banner Carousel List — snippet-author runtime.
 *
 * Responsibilities:
 *   1. Scroll position → progress thumb transform + `.is-active` class on
 *      the centered slide.
 *   2. Prev / next arrow handlers (one slide per click).
 *   3. Optional autoplay with IntersectionObserver pause (the carousel
 *      only auto-advances while it's in the viewport and not under a
 *      hover / focus).
 *   4. Slide-impression + click analytics.
 *
 * Container-scoped: every DOM read/write goes through `node` so multiple
 * carousels on the same page never collide.
 */
;(() => {
  const SNIPPET_ID = 'q2qrnyoo'
  const FEATURE_SLUG = 'banner_carousel_list'
  const ROW_SELECTOR = `.sai-${SNIPPET_ID}__row`
  const SLIDE_SELECTOR = `.sai-${SNIPPET_ID}__slide`
  const PROGRESS_SELECTOR = `.sai-${SNIPPET_ID}__progress`
  const THUMB_SELECTOR = '[data-spectrum-progress-thumb]'
  const ARROW_PREV_SELECTOR = '[data-spectrum-arrow="prev"]'
  const ARROW_NEXT_SELECTOR = '[data-spectrum-arrow="next"]'
  const ACTIVE_CLASS = 'is-active'

  function noopTrack() {}

  // Wrap track so a malformed payload or downstream throw can never break
  // the carousel. Telemetry is observational — surface the failure in
  // DevTools but keep scroll / nav working.
  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[${FEATURE_SLUG}] analytics dispatch failed`, name, err)
      }
    }
  }

  // Find the slide whose center is closest to the viewport's horizontal
  // center. Reference is the VIEWPORT (not the row) so this stays
  // correct even when theme CSS lets the row overflow horizontally — in
  // that case the user effectively scrolls the page/section and the row's
  // bounding box can extend past the viewport, but the slides still move
  // relative to the viewport. Returns 0 if no slides.
  function currentIndex(_row, slides) {
    if (!slides || slides.length === 0) return 0
    const viewportCenter = window.innerWidth / 2
    let bestIndex = 0
    let bestDistance = Number.POSITIVE_INFINITY
    for (let i = 0; i < slides.length; i++) {
      const r = slides[i].getBoundingClientRect()
      const c = r.left + r.width / 2
      const d = Math.abs(c - viewportCenter)
      if (d < bestDistance) {
        bestDistance = d
        bestIndex = i
      }
    }
    return bestIndex
  }

  // Distance to scroll for one slide step — use the second slide's offset
  // if present (handles gap), else the first slide's width as a fallback.
  function slideStride(slides) {
    if (!slides || slides.length < 2) {
      return slides?.[0]?.getBoundingClientRect().width ?? 0
    }
    const a = slides[0].getBoundingClientRect()
    const b = slides[1].getBoundingClientRect()
    return b.left - a.left
  }

  function initCarousel(node, track) {
    const row = node.querySelector(ROW_SELECTOR)
    if (!row) return
    const slides = Array.from(row.querySelectorAll(`:scope > li > ${SLIDE_SELECTOR}`))
    if (slides.length === 0) return

    const total = slides.length
    const thumb = node.querySelector(THUMB_SELECTOR)
    const progress = node.querySelector(PROGRESS_SELECTOR)
    const prevBtn = node.querySelector(ARROW_PREV_SELECTOR)
    const nextBtn = node.querySelector(ARROW_NEXT_SELECTOR)

    // Force the row's overflow + display via inline styles with
    // `!important`. Themes that override `ul { overflow: visible }` or
    // `ul { display: block }` win against author-CSS even with
    // `!important` in some cascade orderings — inline styles with
    // `!important` are the strongest layer the runtime can install.
    row.style.setProperty('display', 'flex', 'important')
    row.style.setProperty('overflow-x', 'auto', 'important')
    row.style.setProperty('overflow-y', 'hidden', 'important')
    // Positioning context so each slide's <li> reports `offsetLeft` relative to
    // the row (used by centerSlideInRow below).
    row.style.setProperty('position', 'relative', 'important')

    // Center a slide within the ROW's own horizontal scroll — never the page.
    // We scroll the row to the slide's <li> layout centre. Using the layout box
    // (`offsetLeft`/`offsetWidth`) keeps centring immune to the active slide's
    // `transform: scale()` (coverflow) and to the current scroll offset, so every
    // slide — including the LAST one — lands centred. The browser clamps `left`
    // into range; the edge clones provide enough trailing room to centre the last
    // real slide. (The previous `scrollIntoView({ block:'nearest', inline:'center' })`
    // also scrolled the PAGE vertically to the banner whenever it sat below the
    // fold — the "page jumps to the carousel on load/scroll" bug.)
    // eslint-disable-next-line no-unused-vars
    const centerSlideInRow = (slide, _behavior) => {
      const li = slide.parentElement
      if (!li) return
      const target = li.offsetLeft + li.offsetWidth / 2 - row.clientWidth / 2
      // Always INSTANT. The row's `scroll-snap-type: x mandatory` cancels programmatic
      // *smooth* scrolls — the snap fires mid-animation and yanks the row back to its
      // current snap point, so a smooth arrow/autoplay step never moves and the slide
      // never centres (the last slide was stuck at scrollLeft 0). Instant lands directly
      // on the target (itself a slide snap point) every time, including the last slide.
      row.scrollTo({ left: Math.max(0, target), behavior: 'auto' })
    }

    // Visual infinite-carousel — clone the last slide before the first,
    // and the first slide after the last. The clones aren't tracked in
    // `slides[]`, IntersectionObserver, or analytics — they're pure
    // decoration so the wrap-around reads as a peek of the neighbor
    // instead of empty space at the edges. The arrow wrap logic
    // (modulo arithmetic + instant scroll on the wrap step) handles the
    // jump between real edges; clones make it look continuous.
    const firstLi = row.children[0]
    const lastLi = row.children[total - 1]
    if (firstLi && lastLi && total > 1) {
      const lastClone = lastLi.cloneNode(true)
      const firstClone = firstLi.cloneNode(true)
      for (const cloneLi of [lastClone, firstClone]) {
        cloneLi.dataset.spectrumClone = 'true'
        cloneLi.setAttribute('aria-hidden', 'true')
        // Strip data-spectrum-banner-index from inner anchor so clones
        // don't get counted as separate impressions / clicks if the
        // observer ever inadvertently picks them up.
        const a = cloneLi.querySelector(`.sai-${SNIPPET_ID}__slide`)
        a?.removeAttribute('data-spectrum-banner-index')
        a?.setAttribute('tabindex', '-1')
      }
      row.insertBefore(lastClone, firstLi)
      row.appendChild(firstClone)
    }

    // Make sure the FIRST and LAST real slides can reach the viewport centre.
    // Centring a slide needs `clientWidth/2 - slideWidth/2` of room beyond it,
    // and the infinite-loop edge clone already supplies `slideWidth + gap` of
    // that. Only add the SHORTFALL the clone can't cover — so on the common
    // case (slide ≳ ⅓ of the row, e.g. 700px cards) the clone alone suffices,
    // we add nothing, and NO blank margin appears (the neighbour clone peeks
    // right up to the centred card). Narrow-slide / very-wide-viewport cases
    // (e.g. coverflow) get a small top-up so the edge cards still centre.
    // Trailing inline padding is unreliable inside a flex scroll container
    // (some engines collapse it), so the end room is a margin on the last DOM
    // child, which always extends scrollWidth. `offsetWidth` is the *layout*
    // width (immune to the active slide's `transform: scale`); `clientWidth`
    // already includes padding so reading it here never feedback-loops.
    const setEdgePadding = () => {
      const first = slides[0]
      if (!first) return
      const sw = first.offsetWidth || first.getBoundingClientRect().width
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0
      const pad = Math.max(0, Math.round(row.clientWidth / 2 - sw / 2 - sw - gap))
      const last = row.lastElementChild
      if (pad > 0) {
        row.style.setProperty('padding-inline', pad + 'px', 'important')
        if (last) last.style.setProperty('margin-inline-end', pad + 'px', 'important')
      } else {
        // No shortfall — leave the row's CSS gutter untouched (no blank edge).
        row.style.removeProperty('padding-inline')
        if (last) last.style.removeProperty('margin-inline-end')
      }
    }

    // Center the real first slide on init. Layout stays stable across
    // variant transitions (sizing is `transform: scale()` only), so a
    // single scroll pass is sufficient.
    const centerRealFirst = () => {
      const first = slides[0]
      if (!first) return
      setEdgePadding()
      suppressScrollSync = true
      centerSlideInRow(first, 'auto')
      window.setTimeout(() => {
        suppressScrollSync = false
      }, 100)
    }
    requestAnimationFrame(centerRealFirst)
    window.addEventListener('resize', centerRealFirst, { passive: true })

    // On-load centering riding a SINGLE requestAnimationFrame is fragile: if that
    // frame runs before layout/fonts/images settle, or while the banner is far
    // below the fold (so rAF is throttled), the first slide never gets centered
    // and sits at scrollLeft 0 — peeking off to one side. Re-run centering on a
    // few robust triggers so it's correct by the time the visitor looks at it,
    // but ONLY while the carousel is still on slide 0 and untouched, so we never
    // yank the visitor back to slide 1 after they (or autoplay) navigate.
    let userInteracted = false
    const markInteracted = () => {
      userInteracted = true
    }
    row.addEventListener('pointerdown', markInteracted, { passive: true })
    row.addEventListener('wheel', markInteracted, { passive: true })
    prevBtn?.addEventListener('click', markInteracted)
    nextBtn?.addEventListener('click', markInteracted)
    const recenterIfUntouched = () => {
      if (!userInteracted && currentIdx === 0) centerRealFirst()
    }
    window.addEventListener('load', recenterIfUntouched)
    window.setTimeout(recenterIfUntouched, 200)
    window.setTimeout(recenterIfUntouched, 600)
    if (typeof IntersectionObserver !== 'undefined') {
      // Center the moment the banner first scrolls into view — guarantees the
      // first slide is centred by the time the visitor actually sees the banner,
      // independent of page-load timing.
      const centerOnEnter = new IntersectionObserver(
        (entries) => {
          for (const e of entries) if (e.isIntersecting) recenterIfUntouched()
        },
        { threshold: 0.01 },
      )
      centerOnEnter.observe(node)
    }

    // Current index lives in JS state, NOT in the scroll offset. This lets
    // arrow clicks advance the thumb + .is-active class even when the row
    // has no scrollable extent (e.g. all slides fit inside the viewport,
    // which is common in narrow Studio iframes). Scroll events still
    // sync state back when the user drags / swipes the row directly.
    let currentIdx = 0
    let suppressScrollSync = false

    // Wrap-aware offset: for index `i` relative to `currentIdx`, return a
    // signed offset in the range (−total/2, +total/2]. So in a 5-slide
    // carousel where currentIdx=0, slides 1..4 map to +1, +2, −2, −1
    // instead of +1, +2, +3, +4 — which matches the visual layout once
    // clones make the wrap continuous on both sides.
    function relativeOffset(i) {
      let offset = i - currentIdx
      const half = total / 2
      if (offset > half) offset -= total
      else if (offset < -half) offset += total
      return offset
    }

    function paint() {
      for (let i = 0; i < slides.length; i++) {
        slides[i].classList.toggle(ACTIVE_CLASS, i === currentIdx)
        // Clamp visible position to [-2, +2]; further slides share the
        // ±2 styling (smallest peripherals).
        const offset = relativeOffset(i)
        const pos = Math.max(-2, Math.min(2, offset))
        slides[i].dataset.position = String(pos)
      }
      // Also paint the leading + trailing clones so they pick up the
      // right "neighbour" transform for coverflow / center-large. The
      // leading clone (= last real slide) always sits one slot to the
      // left of slide 0; trailing clone (= first real slide) sits one
      // slot to the right of the last real slide.
      const cloneEls = row.querySelectorAll(
        `[data-spectrum-clone="true"] .sai-${SNIPPET_ID}__slide`,
      )
      if (cloneEls.length >= 2) {
        // First clone in DOM order is the leading (prepended last slide).
        const leadingPos = currentIdx === 0 ? -1 : -1 - currentIdx
        const trailingPos = currentIdx === total - 1 ? 1 : total - currentIdx
        cloneEls[0].dataset.position = String(Math.max(-2, Math.min(2, leadingPos)))
        cloneEls[1].dataset.position = String(Math.max(-2, Math.min(2, trailingPos)))
      }
      if (thumb && total > 0) {
        thumb.style.transform = `translateX(${currentIdx * 100}%)`
      }
      if (progress) {
        progress.setAttribute('aria-valuenow', String(currentIdx))
      }
      node.dataset.activeIndex = String(currentIdx)
    }

    function setIndex(next, source) {
      const clamped = Math.max(0, Math.min(total - 1, next))
      if (clamped === currentIdx && source !== 'init') return
      currentIdx = clamped
      paint()
      if (track) {
        const slide = slides[currentIdx]
        track(`${FEATURE_SLUG}:slide_change_elem_clicked`, {
          banner_index: currentIdx,
          title: slide?.querySelector(`.sai-${SNIPPET_ID}__title`)?.textContent?.trim() || '',
          url: slide?.getAttribute('href') || '',
        })
      }
    }

    // Scroll the chosen slide into the row's visible area. A no-op when
    // there's no scrollable extent (slides already in view) — which is
    // why the thumb update lives in `paint()` above, not here.
    //
    // `instant` is set on wrap (e.g. last → first via the next arrow): a
    // smooth scroll back through every intermediate slide reads as
    // "going backwards" rather than "looping." Instant jump avoids that.
    //
    // Layout-stable: variants use `transform: scale()` for the size diff,
    // so the row's box model never changes during the animation. A single
    // scroll pass lands at the correct centred position.
    function scrollToCurrent(instant = false) {
      const slide = slides[currentIdx]
      if (!slide) return
      suppressScrollSync = true
      centerSlideInRow(slide, instant ? 'auto' : 'smooth')
      window.setTimeout(
        () => {
          suppressScrollSync = false
        },
        instant ? 100 : 600,
      )
    }

    paint()

    // eslint-disable-next-line no-console
    console.log(`[${FEATURE_SLUG}] init`, { total, rowOverflowX: getComputedStyle(row).overflowX })

    // Track which slide is closest to the viewport center via
    // IntersectionObserver. Fires whenever any slide's visibility
    // changes, regardless of which element is doing the scrolling
    // (row internal scroll, page scroll, parent scroll — all caught).
    // The threshold list gives us callbacks at multiple visibility
    // ratios so we recompute often during a scroll.
    let rafId = 0
    const recompute = () => {
      if (suppressScrollSync) return
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        const idx = currentIndex(row, slides)
        if (idx !== currentIdx) setIndex(idx, 'observer')
      })
    }
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver(recompute, {
        threshold: [0, 0.25, 0.5, 0.75, 1],
      })
      for (const slide of slides) io.observe(slide)
    }
    // Belt-and-suspenders scroll listeners — IO covers visibility
    // changes; these catch any residual cases (programmatic scrolls,
    // bursty scroll where IO debounces, etc.).
    row.addEventListener('scroll', recompute, { passive: true })
    window.addEventListener('scroll', recompute, { passive: true })
    document.addEventListener('scroll', recompute, { passive: true, capture: true })

    // Arrows wrap around — prev from first goes to last, next from last
    // goes to first. Wrap = instant scroll so the carousel doesn't read
    // as "scrolling backwards through every intermediate slide".
    prevBtn?.addEventListener('click', () => {
      const wrapping = currentIdx === 0
      setIndex((currentIdx - 1 + total) % total, 'arrow')
      scrollToCurrent(wrapping)
    })
    nextBtn?.addEventListener('click', () => {
      const wrapping = currentIdx === total - 1
      setIndex((currentIdx + 1) % total, 'arrow')
      scrollToCurrent(wrapping)
    })

    // Click tracking — fires before the browser commits the navigation so
    // the event lands in PostHog. Native link semantics (cmd-click,
    // middle-click) still work — we never preventDefault.
    if (track) {
      for (const slide of slides) {
        slide.addEventListener('click', () => {
          const idx = Number(slide.dataset.spectrumBannerIndex || '0')
          track(`${FEATURE_SLUG}:click_elem_clicked`, {
            banner_index: idx,
            title: slide.querySelector(`.sai-${SNIPPET_ID}__title`)?.textContent?.trim() || '',
            url: slide.getAttribute('href') || '',
          })
        })
      }
    }

    // Slide impression — fires once per slide per page-load when ≥50%
    // visible. Gated by the per-brand impressions kill-switch via the
    // `*_impression` event-name suffix (see snippet-library CLAUDE.md).
    if (track && typeof IntersectionObserver !== 'undefined') {
      const seen = new Set()
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue
            const slide = e.target
            const idx = Number(slide.dataset.spectrumBannerIndex || '0')
            if (seen.has(idx)) continue
            seen.add(idx)
            track(`${FEATURE_SLUG}:slide_impression`, {
              banner_index: idx,
              title: slide.querySelector(`.sai-${SNIPPET_ID}__title`)?.textContent?.trim() || '',
              url: slide.getAttribute('href') || '',
            })
          }
        },
        { threshold: 0.5, root: row },
      )
      for (const slide of slides) io.observe(slide)
    }

    // Autoplay — gated by data-autoplay; paused while the row is hovered
    // or focused, or when the section is offscreen. Drives the same
    // `setIndex` state as arrow clicks so the thumb advances regardless
    // of whether the row has scrollable extent.
    if (node.dataset.autoplay === 'true' && total > 1) {
      const interval = Math.max(2000, Math.min(15000, Number(node.dataset.autoplayMs) || 5000))
      let timer = 0
      let paused = false
      let inView = true

      const tick = () => {
        if (paused || !inView) return
        const wrapping = currentIdx === total - 1
        setIndex((currentIdx + 1) % total, 'autoplay')
        scrollToCurrent(wrapping)
      }
      const start = () => {
        stop()
        timer = window.setInterval(tick, interval)
      }
      const stop = () => {
        if (timer) {
          window.clearInterval(timer)
          timer = 0
        }
      }

      node.addEventListener('mouseenter', () => {
        paused = true
        stop()
      })
      node.addEventListener('mouseleave', () => {
        paused = false
        start()
      })
      node.addEventListener('focusin', () => {
        paused = true
        stop()
      })
      node.addEventListener('focusout', () => {
        paused = false
        start()
      })

      if (typeof IntersectionObserver !== 'undefined') {
        const vio = new IntersectionObserver(
          (entries) => {
            inView = entries[0]?.isIntersecting === true
            if (inView && !paused) start()
            else stop()
          },
          { threshold: 0.25 },
        )
        vio.observe(node)
      } else {
        start()
      }
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiQ2qrnyoo = {
      currentIndex,
      slideStride,
      safeTrack,
    }
  }

  const snippetApi = window.__spectrumAi?.snippet
  const containers = document.querySelectorAll(
    `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
  )
  for (const node of containers) {
    let track = noopTrack
    if (snippetApi && typeof snippetApi.bind === 'function') {
      const handles = snippetApi.bind(node, () => {
        // No variant-resolution work needed — the row is server-rendered
        // and every prop the runtime might swap is captured by the wrapper
        // re-render on variant change.
      })
      if (handles?.track) track = safeTrack(handles.track)
    }
    initCarousel(node, track)
  }
})()
